import { Pencil, Plus } from "lucide-react";
import { Link } from "react-router";

import { SettingsNav } from "@/components/layout/settings-nav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useCanteiros } from "@/features/canteiros/hooks/use-canteiros.ts";
import { canteiroLocation } from "@/features/canteiros/utils.ts";
import { useObras } from "@/features/stages/hooks/use-obras.ts";
import { countObrasByCanteiro } from "@/features/stages/obra.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";

export function CanteirosSettingsPage() {
	const { has } = usePermissions();
	const canUpdate = has("worksites:update");
	const canCreate = has("worksites:create");

	const canteirosQuery = useCanteiros();
	const obrasQuery = useObras();

	const canteiros = canteirosQuery.data ?? [];
	const obrasByCanteiro = countObrasByCanteiro(obrasQuery.data ?? []);

	return (
		<div className="canteiros-page">
			<SettingsNav />

			<div className="page-header">
				<div>
					<h1 className="page-title">Canteiros</h1>
					<p className="page-subtitle">
						Nome, endereço, área e datas dos canteiros da conta.
					</p>
				</div>
				{canCreate && (
					<Button asChild>
						<Link to="/novo-canteiro">
							<Plus />
							Novo canteiro
						</Link>
					</Button>
				)}
			</div>

			{canteirosQuery.isPending ? (
				<Card className="canteiros-page__card">
					<div className="canteiros-page__skeleton">
						{Array.from({ length: 3 }, (_, index) => (
							<Skeleton
								// biome-ignore lint/suspicious/noArrayIndexKey: static placeholder
								key={index}
								className="canteiros-page__skeleton-row"
							/>
						))}
					</div>
				</Card>
			) : canteirosQuery.isError ? (
				<Card>
					<div className="card-state">
						<p>Não foi possível carregar os canteiros.</p>
						<Button variant="outline" onClick={() => canteirosQuery.refetch()}>
							Tentar novamente
						</Button>
					</div>
				</Card>
			) : (
				<Card className="canteiros-page__card">
					<Table>
						<TableHeader className="canteiros-page__thead">
							<TableRow>
								<TableHead className="canteiros-page__col-name">Nome</TableHead>
								<TableHead>Local</TableHead>
								<TableHead className="canteiros-page__col-obras">
									Obras
								</TableHead>
								<TableHead className="canteiros-page__col-actions">
									Ações
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{canteiros.length === 0 ? (
								<TableRow>
									<TableCell colSpan={4} className="canteiros-page__empty">
										Nenhum canteiro cadastrado.
									</TableCell>
								</TableRow>
							) : (
								canteiros.map((canteiro) => {
									const location = canteiroLocation(canteiro);
									return (
										<TableRow key={canteiro.id}>
											<TableCell className="canteiros-page__name-cell">
												{canteiro.name}
											</TableCell>
											<TableCell className="canteiros-page__location-cell">
												{location || "—"}
											</TableCell>
											<TableCell className="canteiros-page__obras-cell">
												{obrasByCanteiro.get(canteiro.id) ?? 0}
											</TableCell>
											<TableCell className="canteiros-page__row-actions">
												{canUpdate && (
													<Button asChild variant="outline" size="sm">
														<Link to={`/editar-canteiro/${canteiro.id}`}>
															<Pencil />
															Editar
														</Link>
													</Button>
												)}
											</TableCell>
										</TableRow>
									);
								})
							)}
						</TableBody>
					</Table>
				</Card>
			)}
		</div>
	);
}
